// src/lib/question-reports.ts
//
// Shared constants and validators for the Question Reporting feature.
// Kept framework-free so it can be unit-tested without Next.js / Prisma.

export const VALID_REPORT_REASONS = [
  'incorrect_answer',
  'typo',
  'unclear',
  'offensive',
  'other',
] as const

export type ReportReason = (typeof VALID_REPORT_REASONS)[number]

export const VALID_REPORT_STATUSES = ['open', 'resolved', 'dismissed'] as const

export type ReportStatus = (typeof VALID_REPORT_STATUSES)[number]

/**
 * Friendly labels shown to users and admins in the UI.
 */
export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  incorrect_answer: 'Incorrect answer',
  typo: 'Typo or formatting',
  unclear: 'Confusing wording',
  offensive: 'Offensive content',
  other: 'Other',
}

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
}

export function isValidReason(value: unknown): value is ReportReason {
  return typeof value === 'string' && (VALID_REPORT_REASONS as readonly string[]).includes(value)
}

export function isValidStatus(value: unknown): value is ReportStatus {
  return typeof value === 'string' && (VALID_REPORT_STATUSES as readonly string[]).includes(value)
}

/**
 * Whether a freshly-created report should trigger an admin alert email.
 * Alert only on the FIRST open report for a given question (i.e. the count of
 * open reports for that question is exactly 1 — the one just created), so a
 * flood of students reporting the same bad question yields a single alert, not a
 * stream. `openReportCount` is the post-insert count of open reports for the
 * question. Pure — the caller supplies the count.
 */
export function shouldAlertForNewReport(openReportCount: number): boolean {
  return openReportCount === 1
}

export type ReportInput = {
  questionId?: unknown
  reason?: unknown
  details?: unknown
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; error: string }

/**
 * Validate a create-report payload. Returns either a normalized, typed value
 * or a short error message suitable for returning from an API route.
 */
export function validateReportInput(
  input: ReportInput,
): ValidationResult<{ questionId: string; reason: ReportReason; details: string | null }> {
  const questionId = typeof input.questionId === 'string' ? input.questionId.trim() : ''
  if (!questionId) {
    return { ok: false, error: 'questionId is required' }
  }

  if (!isValidReason(input.reason)) {
    return {
      ok: false,
      error: `reason must be one of: ${VALID_REPORT_REASONS.join(', ')}`,
    }
  }

  let details: string | null = null
  if (typeof input.details === 'string') {
    const trimmed = input.details.trim()
    if (trimmed.length > 2000) {
      return { ok: false, error: 'details must be 2000 characters or fewer' }
    }
    details = trimmed.length > 0 ? trimmed : null
  } else if (input.details != null) {
    return { ok: false, error: 'details must be a string' }
  }

  return { ok: true, value: { questionId, reason: input.reason, details } }
}

/**
 * Parse the `status` query parameter used by the admin list endpoint.
 * Accepts null/undefined (meaning "all") or a valid status string.
 */
export function parseStatusFilter(
  raw: string | null | undefined,
): ValidationResult<ReportStatus | null> {
  if (raw == null || raw === '' || raw === 'all') {
    return { ok: true, value: null }
  }
  if (!isValidStatus(raw)) {
    return {
      ok: false,
      error: `status must be one of: ${VALID_REPORT_STATUSES.join(', ')}`,
    }
  }
  return { ok: true, value: raw }
}

// ---------------------------------------------------------------------------
// Resolution receipts (what the reporter is told when a report is closed)
// ---------------------------------------------------------------------------

/**
 * How a report was closed. Each kind implies the stored status, so the two can
 * never disagree: we changed something (corrected / removed) → "resolved"; we
 * looked and changed nothing (no_change / duplicate) → "dismissed".
 */
export const VALID_RESOLUTION_KINDS = ['corrected', 'removed', 'no_change', 'duplicate'] as const

export type ResolutionKind = (typeof VALID_RESOLUTION_KINDS)[number]

export function isValidResolution(value: unknown): value is ResolutionKind {
  return typeof value === 'string' && (VALID_RESOLUTION_KINDS as readonly string[]).includes(value)
}

export const RESOLUTION_STATUS: Record<ResolutionKind, Exclude<ReportStatus, 'open'>> = {
  corrected: 'resolved',
  removed: 'resolved',
  no_change: 'dismissed',
  duplicate: 'dismissed',
}

/** Admin-facing labels for the resolution picker. */
export const RESOLUTION_LABELS: Record<ResolutionKind, string> = {
  corrected: 'Corrected — we fixed the question',
  removed: 'Removed — question taken out of the bank',
  no_change: 'No change — the question is correct as written',
  duplicate: 'Duplicate — already being handled',
}

/** Student-facing one-liners (the receipt's headline). */
export const RESOLUTION_STUDENT_LABELS: Record<ResolutionKind, string> = {
  corrected: 'We fixed the question',
  removed: 'We removed the question',
  no_change: 'Reviewed — no change needed',
  duplicate: 'Already being handled',
}

/**
 * Prefilled notes the admin starts from. Written TO the student — the note is
 * shown to them verbatim on /settings/reports and in the dashboard notice.
 */
export const RESOLUTION_NOTE_TEMPLATES: Record<ResolutionKind, string> = {
  corrected:
    'Thanks for flagging this — you were right. We fixed the question, and if it changed how one of your answers was graded, your results have been updated.',
  removed:
    "Thanks for flagging this. We agreed the question wasn't up to standard and removed it from practice and tests.",
  no_change:
    'Thanks for flagging this. We reviewed the question carefully and it is correct as written. The explanation walks through why the keyed answer works.',
  duplicate:
    'Thanks for flagging this. Someone reported the same problem first, and we are handling it there.',
}

export const RESOLUTION_NOTE_MAX = 1000

/** Shown to the student: an open report reads "In review", not "Open". */
export const STUDENT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'In review',
  resolved: 'Resolved',
  dismissed: 'Closed',
}

export type ResolveInput = {
  status: ReportStatus
  resolution: ResolutionKind | null
  resolutionNote: string | null
  notifyReporter: boolean
  applyToQuestion: boolean
}

/**
 * Validate the admin PATCH body. Two shapes:
 *  - `{ resolution, resolutionNote?, notifyReporter?, applyToQuestion? }` — the
 *    receipt flow; the status follows from the kind (RESOLUTION_STATUS).
 *  - `{ status }` alone — the legacy shape (reopen, and older callers). A
 *    close without a resolution notifies nobody: there is nothing to tell.
 * Sending both is allowed only when they agree.
 */
export function validateResolveInput(
  input: Record<string, unknown>,
): ValidationResult<ResolveInput> {
  const { status, resolution, resolutionNote, notifyReporter, applyToQuestion } = input

  if (resolution != null && !isValidResolution(resolution)) {
    return {
      ok: false,
      error: `resolution must be one of: ${VALID_RESOLUTION_KINDS.join(', ')}`,
    }
  }
  if (status != null && !isValidStatus(status)) {
    return { ok: false, error: `status must be one of: ${VALID_REPORT_STATUSES.join(', ')}` }
  }
  if (resolution == null && status == null) {
    return { ok: false, error: `status must be one of: ${VALID_REPORT_STATUSES.join(', ')}` }
  }
  const kind = (resolution ?? null) as ResolutionKind | null
  const implied = kind ? RESOLUTION_STATUS[kind] : (status as ReportStatus)
  if (kind && status != null && status !== implied) {
    return {
      ok: false,
      error: `resolution "${kind}" closes a report as "${implied}", not "${status}"`,
    }
  }

  let note: string | null = null
  if (resolutionNote != null) {
    if (typeof resolutionNote !== 'string') {
      return { ok: false, error: 'resolutionNote must be a string' }
    }
    const trimmed = resolutionNote.trim()
    if (trimmed.length > RESOLUTION_NOTE_MAX) {
      return {
        ok: false,
        error: `resolutionNote must be ${RESOLUTION_NOTE_MAX} characters or fewer`,
      }
    }
    note = trimmed || null
  }
  if (notifyReporter != null && typeof notifyReporter !== 'boolean') {
    return { ok: false, error: 'notifyReporter must be a boolean' }
  }
  if (applyToQuestion != null && typeof applyToQuestion !== 'boolean') {
    return { ok: false, error: 'applyToQuestion must be a boolean' }
  }
  if (implied === 'open' && (kind || note)) {
    return { ok: false, error: 'a reopened report takes no resolution' }
  }

  return {
    ok: true,
    value: {
      status: implied,
      resolution: kind,
      // A kind with no note gets the template, so the student never sees a blank receipt.
      resolutionNote: kind ? (note ?? RESOLUTION_NOTE_TEMPLATES[kind]) : null,
      // Default ON for a real resolution; a legacy status-only close has nothing to say.
      notifyReporter: kind ? notifyReporter !== false : false,
      applyToQuestion: applyToQuestion === true,
    },
  }
}
